import { useRef } from "react";
import type { LinkCardCanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: LinkCardCanvasObject;
}

// OGP リンクカード。
// リッチ（画像 + タイトル）/ ミニマム（タイトルのみ）/ テキストのみ の 3 段階フォールバックで描画する。
// クリックで選択、ドラッグで移動できる。リンクは Ctrl/Cmd + クリックで新規タブを開く挙動に切り替える
// （ドラッグ移動 UX とリンク遷移 UX が衝突するため、明示的なキー操作を要求する）。
export function LinkCardObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) =>
    s.selectedObjectIds.length === 1 && s.selectedObjectIds[0] === object.id
  );

  useObjectDrag({
    objectId: object.id,
    elementRef: ref,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  const hasImage = Boolean(object.imageUrl);

  const onClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.ctrlKey || event.metaKey) {
      window.open(object.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="link-card"
      onClick={onClick}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        background: "var(--color-surface)",
        border: isSelected ? "2px solid var(--color-accent)" : "1px solid var(--color-border)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        textDecoration: "none",
        color: "var(--color-text)",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        cursor: "move",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        userSelect: "none",
      }}
      title={`${object.url}\n（Ctrl/Cmd + クリックで新規タブを開きます）`}
    >
      {hasImage ? (
        <img
          src={object.imageUrl}
          alt=""
          style={{ width: "100%", height: "55%", objectFit: "cover", pointerEvents: "none" }}
          loading="lazy"
          referrerPolicy="no-referrer"
          draggable={false}
        />
      ) : null}
      <div style={{ padding: "12px", flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
        <strong style={{ fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {object.title}
        </strong>
        {object.description ? (
          <span style={{ fontSize: "12px", color: "var(--color-text-muted)" }}>
            {object.description}
          </span>
        ) : null}
        <span style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>
          {object.siteName ?? safeHostname(object.url)}
        </span>
      </div>
    </div>
  );
}

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
